import { use, useState, useTransition } from 'react';

import EditResidentCommand from 'features/residents/models/edit-resident-command';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import { useEditResident } from 'features/residents/resident-settings/providers/resident-queries';

type ResidentSettingDetails = {
  isScrolling: boolean;
  isSubmitting: boolean;
  updateIsLoading: boolean;
  submitForm: () => void;
  handleScroll: (event: React.UIEvent<HTMLElement>) => void;
};

const useResidentSettingDetails = (): ResidentSettingDetails => {
  const { mutateAsync: editResident, isPending: isLoading } = useEditResident();

  const {
    refetchResidentSettings,
    detailEditState: { currentResidentSetting, residentSettingEditForm, enableDetailViewingMode, updateCurrentResidentSetting, resetForm },
  } = use(ResidentSettingsProviderContext);

  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isSubmitting, startSubmitTransition] = useTransition();

  const handleScroll = (event: React.UIEvent<HTMLElement>): void => {
    if (isScrolling && event.currentTarget.scrollTop === 0) {
      setIsScrolling(false);
    } else if (!isScrolling && event.currentTarget.scrollTop > 0) {
      setIsScrolling(true);
    }
  };

  const submitEdit = async (): Promise<void> => {
    const scenariosMuted = residentSettingEditForm.getValues('scenariosMuted');

    if (!scenariosMuted) {
      residentSettingEditForm.setValue('muteScenariosUntil', undefined);
      residentSettingEditForm.setValue('muteScenariosReason', undefined);
    }

    const editResidentCommand: EditResidentCommand = {
      organisationUnitId: currentResidentSetting?.organisationUnitId ?? '',
      editOrganisationUnitScenarios: residentSettingEditForm.getValues('organisationUnitScenarios'),
      deviceIds: residentSettingEditForm.getValues('devices').map((device) => device.id),
      note: residentSettingEditForm.getValues('note')?.trim(),
      muteScenariosReason: residentSettingEditForm.getValues('muteScenariosReason'),
      muteScenariosUntil: residentSettingEditForm.getValues('muteScenariosUntil'),
    };

    try {
      const updatedResident = await editResident(editResidentCommand);

      resetForm(updatedResident);
      updateCurrentResidentSetting(updatedResident);
    } finally {
      refetchResidentSettings();
    }
  };

  const submitForm = (): void => {
    startSubmitTransition(async () => {
      await submitEdit();
      enableDetailViewingMode();
    });
  };

  return {
    isScrolling,
    isSubmitting,
    updateIsLoading: isLoading,
    submitForm,
    handleScroll,
  };
};

export default useResidentSettingDetails;
